<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    NextButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
    },
  },
  data() {
    return {
      isLoading: false,
      app: {
        title: '',
        content: {
          type: 'frame',
          url: '',
        },
      },
      showOnSidebar: false,
      sendInfoToApp: false,
    };
  },
  computed: {
    baseTranslationKey() {
      return `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode.toUpperCase()}`;
    },
    header() {
      return this.$t(`${this.baseTranslationKey}.HEADER`);
    },
    submitButtonLabel() {
      return this.$t(`${this.baseTranslationKey}.FORM_SUBMIT`);
    },
  },
  mounted() {
    if (this.mode === 'UPDATE' && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      this.app.content = this.selectedAppData.content[0];
      this.showOnSidebar = this.selectedAppData.show_on_sidebar;
      this.sendInfoToApp = !!this.selectedAppData.send_credentials;
    }
  },
  methods: {
    closeModal() {
      // Reset the data once closed
      this.app = {
        title: '',
        content: { type: 'frame', url: '' },
      };
      this.showOnSidebar = false;
      this.sendInfoToApp = false;
      this.$emit('close');
    },
    async submit() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        const payload = {
          title: this.app.title,
          content: [this.app.content],
          show_on_sidebar: this.showOnSidebar,
          send_credentials: this.showOnSidebar && this.sendInfoToApp,
        };

        if (action === 'update') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        useAlert(this.$t(`${this.baseTranslationKey}.API_SUCCESS`));
        this.closeModal();
      } catch (err) {
        useAlert(this.$t(`${this.baseTranslationKey}.API_ERROR`));
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" @close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="header" />
      <form class="w-full" @submit.prevent="submit">
        <woot-input
          v-model="app.title"
          :class="{ error: v$.app.title.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
          "
          :error="
            v$.app.title.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
              : null
          "
          data-testid="app-title"
          @input="v$.app.title.$touch"
          @blur="v$.app.title.$touch"
        />
        <woot-input
          v-model="app.content.url"
          :class="{ error: v$.app.content.url.$error }"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
          "
          :error="
            v$.app.content.url.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
              : null
          "
          data-testid="app-url"
          @input="v$.app.content.url.$touch"
          @blur="v$.app.content.url.$touch"
        />
        <div class="flex items-center w-full gap-2 my-2">
          <input v-model="showOnSidebar" type="checkbox" :value="true" />
          <label>
            {{
              $t(
                'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SHOW_ON_SIDEBAR.LABEL'
              )
            }}
          </label>
        </div>
        <div v-if="showOnSidebar" class="flex items-center w-full gap-2 my-2">
          <input v-model="sendInfoToApp" type="checkbox" :value="true" />
          <label>
            {{
              $t(
                'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SEND_INFO_TO_APP.LABEL'
              )
            }}
          </label>
        </div>
        <p
          v-if="showOnSidebar && sendInfoToApp"
          class="text-xs text-n-slate-10 mb-2"
        >
          {{
            $t(
              'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.SEND_INFO_TO_APP.HELP_TEXT'
            )
          }}
        </p>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
